import { Icon20CloudArrowUpOutline } from '@vkontakte/icons';
import { ReactComponent as DropImage } from 'img/albums/drop.svg';
import React, { memo, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isDarkTheme } from 'reactApp/appHelpers/featuresHelpers';
import { inputType } from 'reactApp/appHelpers/featuresHelpers/features/uploadNewFileApi';
import { setUploadFromAction } from 'reactApp/modules/upload/upload.module';
import { getAllowedMimeTypes } from 'reactApp/modules/upload/upload.selectors';
import { useUploadInputHandlers } from 'reactApp/modules/uploading/hooks/useUploadInputHandlers';
import styles from 'reactApp/ui/EmptyFolder/EmptyFolder.css';
import { type FolderStorageFlags, EItemType, gaSuffix } from 'reactApp/ui/EmptyFolder/EmptyFolder.types';
import { sendGa } from 'reactApp/utils/ga';

const getText = (folderStorageFlags: FolderStorageFlags) => {
    const { isAlbums, isVigbo } = folderStorageFlags;

    if (isAlbums) {
        return {
            title: 'Загрузить фото и видео',
            annotation: 'Нажмите или перетащите файлы для загрузки в этот альбом',
        };
    }

    if (isVigbo) {
        return {
            title: 'Загрузить фото с компьютера',
            annotation: 'Выберите файлы на компьютере, которые хотите добавить в галерею',
        };
    }

    return {
        title: 'Загрузить файлы',
        annotation: 'Вы также можете перетащить файлы для загрузки в эту папку',
    };
};

export const UploadComponent = memo(() => {
    const allowedMimeTypes = useSelector(getAllowedMimeTypes)?.join(',');
    const inputRef = useRef<HTMLInputElement>(null);

    useUploadInputHandlers({ input: inputRef });

    return (
        <div className={styles.icon}>
            <input type={inputType} multiple className={styles.input} ref={inputRef} accept={allowedMimeTypes} />
            <Icon20CloudArrowUpOutline />
        </div>
    );
});

UploadComponent.displayName = 'UploadComponent';

export const useUpload = (folderStorageFlags: FolderStorageFlags) => {
    const { isAlbums } = folderStorageFlags;

    const dispatch = useDispatch();

    const handleUpload = useCallback(() => {
        sendGa(gaSuffix, 'upload');
        dispatch(setUploadFromAction('empty-folder'));
    }, []);

    const text = getText(folderStorageFlags);

    const additionLinks = useMemo(
        () =>
            isAlbums ? (
                <div className={styles.uploadArea}>
                    <DropImage
                        className={styles.uploadAreaDrop}
                        stroke={isDarkTheme ? '#616161' : '#000'}
                        stroke-opacity={isDarkTheme ? '1' : '0.2'}
                    />
                    <span className={styles.uploadAreaText}>
                        <a className={styles.link} onClick={handleUpload}>
                            Нажмите
                        </a>{' '}
                        или&nbsp;перенесите файлы для&nbsp;загрузки
                    </span>
                </div>
            ) : undefined,
        [isAlbums, handleUpload]
    );

    return useCallback(() => {
        return {
            ...text,
            onClick: handleUpload,
            type: EItemType.UPLOAD,
            Icon: <UploadComponent />,
            additionLinks,
        };
    }, [additionLinks, text]);
};
